import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Moon, Sun, Monitor, Accessibility, LogOut, PackageCheck, Hourglass, ShoppingBag, Menu } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import { api } from "../lib/api";

interface NotificationSummary {
  pendingApprovals: number;
  pendingPickUp: number;
  readyToShip: number;
}

export function TopBar({ title, subtitle, onMenuClick }: { title: string; subtitle?: string; onMenuClick: () => void }) {
  const { user, can, logout } = useAuth();
  const { mode, setMode, a11yLarge, setA11yLarge } = useTheme();
  const navigate = useNavigate();

  const [openMenu, setOpenMenu] = useState<"user" | "notifications" | null>(null);
  const [summary, setSummary] = useState<NotificationSummary | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((p) => p[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "?";

  const nextMode = mode === "light" ? "dark" : mode === "dark" ? "system" : "light";
  const ThemeIcon = mode === "light" ? Sun : mode === "dark" ? Moon : Monitor;

  // Real counts (order_history's notification-summary — the same source Order History/Alerts
  // already use), fetched once so the bell's dot only ever appears when there's something real to
  // show. A role without order_history.read simply never sees a dot, rather than a fabricated one.
  useEffect(() => {
    if (!can("order_history.read")) return;
    let cancelled = false;
    api
      .get<NotificationSummary>("/orders/notification-summary")
      .then((s) => {
        if (!cancelled) setSummary(s);
      })
      .catch(() => {
        /* leave summary null — the dot simply won't show */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!openMenu) return;
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpenMenu(null);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [openMenu]);

  const totalNotifications = summary ? summary.pendingApprovals + summary.readyToShip + summary.pendingPickUp : 0;

  return (
    <header className="sticky top-0 z-20 bg-card border-b border-border px-4 sm:px-7 py-3.5 flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-3 min-w-0">
        <button
          onClick={onMenuClick}
          className="lg:hidden w-9 h-9 flex-shrink-0 rounded-full border border-border bg-card flex items-center justify-center text-ink-muted"
          aria-label="Open menu"
        >
          <Menu size={18} />
        </button>
        <div className="min-w-0">
          <div className="text-xl font-extrabold tracking-tight truncate">{title}</div>
          {subtitle && <div className="text-[13px] text-ink-muted mt-0.5 truncate">{subtitle}</div>}
        </div>
      </div>
      <div className="flex items-center gap-2.5" ref={containerRef}>
        <button
          onClick={() => setA11yLarge(!a11yLarge)}
          aria-pressed={a11yLarge}
          title="Visually Impaired Mode"
          className={`w-9 h-9 rounded-full border border-border flex items-center justify-center ${a11yLarge ? "bg-brand-blue text-white" : "bg-card text-ink-muted"}`}
        >
          <Accessibility size={17} />
        </button>
        <button onClick={() => setMode(nextMode)} title={`Theme: ${mode}`} className="w-9 h-9 rounded-full border border-border bg-card flex items-center justify-center text-ink-muted">
          <ThemeIcon size={17} />
        </button>

        <div className="relative">
          <button
            className="relative w-9 h-9 rounded-full border border-border bg-card flex items-center justify-center text-ink-muted"
            aria-label="Notifications"
            aria-expanded={openMenu === "notifications"}
            onClick={() => setOpenMenu((m) => (m === "notifications" ? null : "notifications"))}
          >
            <Bell size={17} />
            {totalNotifications > 0 && <span className="absolute top-1.5 right-2 w-2 h-2 rounded-full bg-status-red border border-card" />}
          </button>
          {openMenu === "notifications" && (
            <div className="absolute right-0 mt-2 w-80 card shadow-pop py-2 z-30">
              <div className="px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-ink-muted">Notifications</div>
              {!can("order_history.read") ? (
                <div className="px-4 py-3 text-sm text-ink-muted">Nothing to show for your role.</div>
              ) : !summary ? (
                <div className="px-4 py-3 text-sm text-ink-muted">Loading…</div>
              ) : totalNotifications === 0 ? (
                <div className="px-4 py-3 text-sm text-ink-muted">You're all caught up.</div>
              ) : (
                <div className="flex flex-col">
                  {summary.pendingApprovals > 0 && (
                    <button
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-surface text-left"
                      onClick={() => {
                        setOpenMenu(null);
                        navigate("/orders");
                      }}
                    >
                      <Hourglass size={16} className="text-status-orange flex-shrink-0" />
                      <span className="text-sm">
                        <strong>{summary.pendingApprovals}</strong> order{summary.pendingApprovals === 1 ? "" : "s"} pending approval
                      </span>
                    </button>
                  )}
                  {summary.readyToShip > 0 && (
                    <button
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-surface text-left"
                      onClick={() => {
                        setOpenMenu(null);
                        navigate("/shipping");
                      }}
                    >
                      <PackageCheck size={16} className="text-brand-blue flex-shrink-0" />
                      <span className="text-sm">
                        <strong>{summary.readyToShip}</strong> order{summary.readyToShip === 1 ? "" : "s"} ready to ship
                      </span>
                    </button>
                  )}
                  {summary.pendingPickUp > 0 && (
                    <button
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-surface text-left"
                      onClick={() => {
                        setOpenMenu(null);
                        navigate("/orders");
                      }}
                    >
                      <ShoppingBag size={16} className="text-status-green flex-shrink-0" />
                      <span className="text-sm">
                        <strong>{summary.pendingPickUp}</strong> order{summary.pendingPickUp === 1 ? "" : "s"} awaiting customer pickup
                      </span>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="relative">
          <button
            className="w-9 h-9 rounded-full bg-brand-blue text-white flex items-center justify-center text-[13px] font-bold"
            title={user?.name}
            aria-expanded={openMenu === "user"}
            onClick={() => setOpenMenu((m) => (m === "user" ? null : "user"))}
          >
            {initials}
          </button>
          {openMenu === "user" && (
            <div className="absolute right-0 mt-2 w-56 card shadow-pop py-2 z-30">
              <div className="px-4 py-2">
                <div className="text-sm font-bold">{user?.name}</div>
                <div className="text-xs text-ink-muted">{user?.role.replace(/_/g, " ")}</div>
              </div>
              <div className="border-t border-border my-1.5" />
              <button
                className="flex items-center gap-2.5 w-full text-left px-4 py-2 text-sm font-semibold text-status-red hover:bg-surface"
                onClick={() => {
                  setOpenMenu(null);
                  void logout();
                }}
              >
                <LogOut size={15} />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
